import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { FulfilmentService } from '../../../services/fulfilment.service';
import {
  PACKAGE_TYPE_SUGGESTIONS, PackageRequest, PackagesView, SHIPMENT_NEXT, SHIPMENT_STATUS_COLORS, SalesPackage, Shipment,
  ShipmentStatus, ShipmentView, isHttpUrl,
} from '../../../interfaces/sales/fulfilment.interface';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { humanizeEnum } from '../../../utils/format';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';

/** F7: the shipment states the server will mail (mirrors ShipmentEmailService.MAILABLE). */
const MAILABLE: ShipmentStatus[] = ['SHIPPED', 'IN_TRANSIT'];

const qty = (n?: number | null) => (n == null ? '' : String(Number(n)));
const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));

/** D-P2-3: tracking links only for http(s), opened without an opener. */
export const TrackingLink: React.FC<{ url?: string | null; label?: string }> = ({ url, label }) =>
  isHttpUrl(url)
    ? <Link href={url} target="_blank" rel="noopener noreferrer">{label ?? 'Track'}</Link>
    : null;

/** F5: "Packages & shipment" tab of a delivery challan. */
const ChallanFulfilmentTab: React.FC<{ challanId: string }> = ({ challanId }) => {
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SHIPMENT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution();
  const [pv, setPv] = useState<PackagesView | null>(null);
  const [sv, setSv] = useState<ShipmentView | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<SalesPackage | 'new' | null>(null);
  const [deleting, setDeleting] = useState<SalesPackage | null>(null);
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [statusTo, setStatusTo] = useState<ShipmentStatus | null>(null);
  const [mailOpen, setMailOpen] = useState(false);   // F7

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        FulfilmentService.packages(institutionId, challanId), FulfilmentService.shipment(institutionId, challanId),
      ]);
      setPv(p);
      setSv(s);
    } catch (e) {
      toast.showApiError(e, 'Failed to load packages and shipment');
    }
  }, [institutionId, challanId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    try { setBusy(true); await fn(); toast.showSuccess(ok); await load(); } catch (e) { toast.showApiError(e, fail); } finally { setBusy(false); }
  };
  const slip = async () => {
    try {
      const blob = await FulfilmentService.packingSlip(institutionId, challanId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.showApiError(e, 'Failed to download the packing slip'); }
  };

  if (!pv || !sv) return <Box sx={{ p: 2 }}><CircularProgress /></Box>;
  const shipment = sv.shipment ?? null;
  const cancelledChallan = pv.challanStatus === 'CANCELLED';
  const anyRemaining = pv.lines.some(l => l.packable && Number(l.remainingQty) > 0);
  const canEditPackages = canManage && pv.editable;
  const nextStatuses: ShipmentStatus[] = !shipment ? [] : cancelledChallan
    ? (['PENDING', 'SHIPPED'].includes(shipment.status) ? ['CANCELLED'] : [])
    : SHIPMENT_NEXT[shipment.status];

  return (
    <Box data-help-page="sales.fulfilment.page">
      {cancelledChallan && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This challan is cancelled. Its packages can no longer be changed{shipment && nextStatuses.length > 0 ? '; cancel the shipment below' : ''}.
        </Alert>
      )}
      {pv.challanStatus === 'DRAFT' && <Alert severity="info" sx={{ mb: 2 }}>Packages and a shipment can be added once the challan is posted.</Alert>}
      {pv.challanStatus === 'POSTED' && pv.lockReason && <Alert severity="info" sx={{ mb: 2 }}>{pv.lockReason}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }} data-help-section="sales.fulfilment.packages">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="h6">Packages</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canManage && (
              <>
                <Button data-help="sales.fulfilment.addPackage" disabled={!canEditPackages || !anyRemaining || busy} onClick={() => setEditing('new')}>Add package</Button>
                <Button data-help="sales.fulfilment.autoPack" disabled={!canEditPackages || !anyRemaining || busy}
                  onClick={() => run(() => FulfilmentService.autoPack(institutionId, challanId, 'Carton'), 'Remaining quantities packed', 'Failed to auto-pack')}>
                  Auto-pack remaining
                </Button>
              </>
            )}
            <Button data-help="sales.fulfilment.packingSlip" disabled={pv.packages.length === 0 || pv.challanStatus !== 'POSTED'} onClick={slip}>Packing slip</Button>
          </Box>
        </Box>
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="sales.fulfilment.col.item">Item</TableCell><TableCell align="right" data-help="sales.fulfilment.col.onChallan">On challan</TableCell><TableCell align="right" data-help="sales.fulfilment.col.packed">Packed</TableCell><TableCell align="right" data-help="sales.fulfilment.col.left">Left to pack</TableCell></TableRow></TableHead>
          <TableBody>
            {pv.lines.map(l => (
              <TableRow key={l.challanLineId}>
                <TableCell>{l.packable ? `${l.itemCode ?? ''} - ${l.itemName ?? ''}` : `${l.description ?? l.itemName ?? 'Service'} (service, not packed)`}</TableCell>
                <TableCell align="right">{qty(l.challanQty)}{l.unit ? ` ${l.unit}` : ''}</TableCell>
                <TableCell align="right">{l.packable ? qty(l.packedQty) : '-'}</TableCell>
                <TableCell align="right">{l.packable ? qty(l.remainingQty) : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pv.packages.length === 0 && <Typography sx={{ mt: 2 }} color="text.secondary">No packages yet.</Typography>}
        {pv.packages.map(p => (
          <Paper key={p.id} variant="outlined" sx={{ p: 1.5, mt: 2 }} data-testid="package-card">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography fontWeight={600}>
                Package {p.seq} of {pv.packages.length}{p.packageType ? ` · ${p.packageType}` : ''}
              </Typography>
              {canEditPackages && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" data-help="sales.fulfilment.editPackage" onClick={() => setEditing(p)}>Edit</Button>
                  <Button size="small" color="error" data-help="sales.fulfilment.deletePackage" onClick={() => setDeleting(p)}>Delete</Button>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {[p.grossWeightKg != null ? `Gross ${qty(p.grossWeightKg)} kg` : null, p.netWeightKg != null ? `Net ${qty(p.netWeightKg)} kg` : null,
                p.lengthCm != null || p.widthCm != null || p.heightCm != null ? `${qty(p.lengthCm) || '-'} × ${qty(p.widthCm) || '-'} × ${qty(p.heightCm) || '-'} cm` : null,
                p.notes || null].filter(Boolean).join(' · ')}
            </Typography>
            {p.lines.map(l => (
              <Typography key={l.id} variant="body2">{l.itemCode} - {l.itemName}: <b>{qty(l.quantity)}{l.unit ? ` ${l.unit}` : ''}</b></Typography>
            ))}
          </Paper>
        ))}
      </Paper>

      <Paper sx={{ p: 2 }} data-help-section="sales.fulfilment.shipment">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="h6">Shipment</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canManage && !shipment && pv.challanStatus === 'POSTED' && (
              <Button variant="contained" data-help="sales.fulfilment.createShipment" disabled={busy} onClick={() => setShipmentOpen(true)}>Create shipment</Button>
            )}
            {canManage && shipment && !cancelledChallan && shipment.status !== 'DELIVERED' && shipment.status !== 'RETURNED' && (
              <Button data-help="sales.fulfilment.editShipment" disabled={busy} onClick={() => setShipmentOpen(true)}>Edit details</Button>
            )}
            {/* F7: an explicit, confirmed click; nothing is mailed automatically; never for a cancelled challan */}
            {canManage && shipment && !cancelledChallan && MAILABLE.includes(shipment.status) && (
              <Button data-help="sales.fulfilment.email" disabled={busy} onClick={() => setMailOpen(true)}>E-mail customer</Button>
            )}
            {canManage && nextStatuses.map(s => (
              <Button key={s} color={s === 'CANCELLED' ? 'error' : 'primary'} data-help="sales.fulfilment.status" disabled={busy} onClick={() => setStatusTo(s)}>
                {s === 'CANCELLED' ? 'Cancel shipment' : `Mark ${humanizeEnum(s).toLowerCase()}`}
              </Button>
            ))}
          </Box>
        </Box>
        {shipment ? <ShipmentSummary s={shipment} /> : <Typography color="text.secondary">No shipment.</Typography>}
        {sv.history.filter(h => h.status === 'CANCELLED').length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Cancelled shipments</Typography>
            {sv.history.filter(h => h.status === 'CANCELLED').map(h => (
              <Typography key={h.id} variant="body2" color="text.secondary">{h.shipmentNo}{h.carrier ? ` · ${h.carrier}` : ''}{h.trackingNo ? ` · ${h.trackingNo}` : ''}</Typography>
            ))}
          </Box>
        )}
      </Paper>

      {editing && (
        <PackageDialog view={pv} pkg={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
          onSave={dto => {
            const target = editing;
            setEditing(null);
            return run(() => (target === 'new'
              ? FulfilmentService.createPackage(institutionId, challanId, dto)
              : FulfilmentService.updatePackage(institutionId, challanId, target.id, dto)), 'Package saved', 'Failed to save the package');
          }} />
      )}
      <ConfirmDialog open={!!deleting} title="Delete package" confirmLabel="Delete" confirmColor="error" submitting={busy}
        message={`Delete package ${deleting?.seq ?? ''}? Later packages are renumbered.`}
        onCancel={() => setDeleting(null)}
        onConfirm={() => { const d = deleting!; setDeleting(null); run(() => FulfilmentService.deletePackage(institutionId, challanId, d.id), 'Package deleted', 'Failed to delete the package'); }} />
      {shipmentOpen && (
        <ShipmentDialog shipment={shipment} onClose={() => setShipmentOpen(false)}
          onSave={dto => {
            setShipmentOpen(false);
            return run(() => (shipment ? FulfilmentService.updateShipment(institutionId, challanId, dto)
              : FulfilmentService.createShipment(institutionId, challanId, dto)), 'Shipment saved', 'Failed to save the shipment');
          }} />
      )}
      {/* ---- F7 ---- */}
      <ConfirmDialog open={mailOpen} title="E-mail customer" confirmLabel="Send e-mail" submitting={busy}
        message={`Send the shipment notice for ${shipment?.shipmentNo ?? ''} to the customer's e-mail address on file?`}
        onCancel={() => setMailOpen(false)}
        onConfirm={async () => {
          try {
            setBusy(true);
            const r = await FulfilmentService.emailCustomer(institutionId, challanId);
            toast.showSuccess(`Shipment notice e-mailed to ${r.sentTo}`);
          } catch (e) {
            toast.showApiError(e, 'Failed to e-mail the customer');
          } finally {
            setBusy(false);
            setMailOpen(false);
          }
        }} />
      {/* ---- end F7 ---- */}
      {statusTo && (
        <StatusDialog to={statusTo} challanDate={sv.challanDate} shippedOn={shipment?.shippedOn} onClose={() => setStatusTo(null)}
          onSave={dto => {
            setStatusTo(null);
            return run(() => FulfilmentService.changeStatus(institutionId, challanId, dto), 'Shipment updated', 'Failed to update the shipment');
          }} />
      )}
    </Box>
  );
};

const ShipmentSummary: React.FC<{ s: Shipment }> = ({ s }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
    <Typography>Shipment: <b>{s.shipmentNo}</b> <Chip size="small" label={humanizeEnum(s.status)} color={SHIPMENT_STATUS_COLORS[s.status]} /></Typography>
    {s.carrier && <Typography>Carrier: <b>{s.carrier}</b></Typography>}
    {s.trackingNo && <Typography>Tracking: <b>{s.trackingNo}</b> {isHttpUrl(s.trackingUrl) && <TrackingLink url={s.trackingUrl} />}</Typography>}
    {!s.trackingNo && isHttpUrl(s.trackingUrl) && <Typography><TrackingLink url={s.trackingUrl} label="Tracking link" /></Typography>}
    {s.shippedOn && <Typography>Shipped: <b>{formatDate(s.shippedOn)}</b></Typography>}
    {s.deliveredOn && <Typography>Delivered: <b>{formatDate(s.deliveredOn)}</b>{s.deliveredTo ? ` to ${s.deliveredTo}` : ''}</Typography>}
    {s.notes && <Typography color="text.secondary">{s.notes}</Typography>}
  </Box>
);

const PackageDialog: React.FC<{ view: PackagesView; pkg: SalesPackage | null; onClose: () => void; onSave: (dto: PackageRequest) => void }> =
  ({ view, pkg, onClose, onSave }) => {
    const packable = view.lines.filter(l => l.packable);
    const mine = (lineId: string) => Number(pkg?.lines.find(l => l.challanLineId === lineId)?.quantity ?? 0);
    const max = (lineId: string) => Number(packable.find(l => l.challanLineId === lineId)?.remainingQty ?? 0) + mine(lineId);
    const [type, setType] = useState(pkg?.packageType ?? 'Carton');
    const [gross, setGross] = useState(qty(pkg?.grossWeightKg));
    const [net, setNet] = useState(qty(pkg?.netWeightKg));
    const [l, setL] = useState(qty(pkg?.lengthCm));
    const [w, setW] = useState(qty(pkg?.widthCm));
    const [h, setH] = useState(qty(pkg?.heightCm));
    const [notes, setNotes] = useState(pkg?.notes ?? '');
    const [qtys, setQtys] = useState<Record<string, string>>(() => Object.fromEntries(packable.map(x => [
      x.challanLineId, pkg ? (mine(x.challanLineId) ? String(mine(x.challanLineId)) : '') : (Number(x.remainingQty) > 0 ? String(Number(x.remainingQty)) : ''),
    ])));
    const lines = Object.entries(qtys).filter(([, v]) => v.trim() !== '' && Number(v) > 0).map(([challanLineId, v]) => ({ challanLineId, quantity: Number(v) }));
    const over = lines.some(x => x.quantity > max(x.challanLineId) + 1e-9);
    const negative = [gross, net, l, w, h].some(v => v.trim() !== '' && Number(v) < 0);
    const netOverGross = gross.trim() !== '' && net.trim() !== '' && Number(net) > Number(gross);
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm" data-help-page="sales.fulfilment.pkg.page">
        <DialogTitle>{pkg ? `Edit package ${pkg.seq}` : 'Add package'}</DialogTitle>
        <DialogContent data-help-section="sales.fulfilment.pkg.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete freeSolo options={PACKAGE_TYPE_SUGGESTIONS} value={type} onInputChange={(_, v) => setType(v)}
            renderInput={p => <TextField {...p} data-help="sales.fulfilment.pkg.type" label="Package type" inputProps={{ ...p.inputProps, maxLength: 30 }} sx={{ mt: 1 }} />} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField data-help="sales.fulfilment.pkg.gross" label="Gross weight (kg)" type="number" value={gross} onChange={e => setGross(e.target.value)} sx={{ flex: '1 1 160px' }} />
            <TextField data-help="sales.fulfilment.pkg.net" label="Net weight (kg)" type="number" value={net} onChange={e => setNet(e.target.value)} sx={{ flex: '1 1 160px' }}
              error={netOverGross} helperText={netOverGross ? 'Net cannot exceed gross' : undefined} />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField data-help="sales.fulfilment.pkg.length" label="Length (cm)" type="number" value={l} onChange={e => setL(e.target.value)} sx={{ flex: '1 1 110px' }} />
            <TextField data-help="sales.fulfilment.pkg.width" label="Width (cm)" type="number" value={w} onChange={e => setW(e.target.value)} sx={{ flex: '1 1 110px' }} />
            <TextField data-help="sales.fulfilment.pkg.height" label="Height (cm)" type="number" value={h} onChange={e => setH(e.target.value)} sx={{ flex: '1 1 110px' }} />
          </Box>
          <Typography variant="subtitle2">Contents</Typography>
          {packable.map(x => (
            <TextField key={x.challanLineId} data-help="sales.fulfilment.pkg.contents" type="number" size="small"
              label={`${x.itemCode ?? ''} - ${x.itemName ?? ''} (up to ${max(x.challanLineId)}${x.unit ? ` ${x.unit}` : ''})`}
              value={qtys[x.challanLineId] ?? ''} onChange={e => setQtys(q => ({ ...q, [x.challanLineId]: e.target.value }))}
              error={Number(qtys[x.challanLineId] || 0) > max(x.challanLineId) + 1e-9} />
          ))}
          <TextField data-help="sales.fulfilment.pkg.notes" label="Notes" value={notes} onChange={e => setNotes(e.target.value)} inputProps={{ maxLength: 500 }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.fulfilment.pkg.cancel" onClick={onClose}>Cancel</Button>
          <Button variant="contained" data-help="sales.fulfilment.pkg.save" disabled={lines.length === 0 || over || negative || netOverGross}
            onClick={() => onSave({
              packageType: type.trim() || undefined, grossWeightKg: num(gross), netWeightKg: num(net), lengthCm: num(l), widthCm: num(w),
              heightCm: num(h), notes: notes.trim() || undefined, lines,
            })}>Save</Button>
        </DialogActions>
      </Dialog>
    );
  };

const ShipmentDialog: React.FC<{ shipment: Shipment | null; onClose: () => void; onSave: (dto: { carrier?: string; trackingNo?: string; trackingUrl?: string; notes?: string }) => void }> =
  ({ shipment, onClose, onSave }) => {
    const [carrier, setCarrier] = useState(shipment?.carrier ?? '');
    const [trackingNo, setTrackingNo] = useState(shipment?.trackingNo ?? '');
    const [trackingUrl, setTrackingUrl] = useState(shipment?.trackingUrl ?? '');
    const [notes, setNotes] = useState(shipment?.notes ?? '');
    const badUrl = trackingUrl.trim() !== '' && !isHttpUrl(trackingUrl.trim());
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-help-page="sales.fulfilment.ship.page">
        <DialogTitle>{shipment ? `Shipment ${shipment.shipmentNo}` : 'Create shipment'}</DialogTitle>
        <DialogContent data-help-section="sales.fulfilment.ship.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField data-help="sales.fulfilment.ship.carrier" label="Carrier" value={carrier} onChange={e => setCarrier(e.target.value)} inputProps={{ maxLength: 100 }} sx={{ mt: 1 }} />
          <TextField data-help="sales.fulfilment.ship.trackingNo" label="Tracking no" value={trackingNo} onChange={e => setTrackingNo(e.target.value)} inputProps={{ maxLength: 100 }} />
          <TextField data-help="sales.fulfilment.ship.trackingUrl" label="Tracking URL" value={trackingUrl} onChange={e => setTrackingUrl(e.target.value)} inputProps={{ maxLength: 500 }}
            error={badUrl} helperText={badUrl ? 'Must start with https:// or http://' : 'Optional'} />
          <TextField data-help="sales.fulfilment.ship.notes" label="Notes" value={notes} onChange={e => setNotes(e.target.value)} inputProps={{ maxLength: 500 }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.fulfilment.ship.cancel" onClick={onClose}>Cancel</Button>
          <Button variant="contained" data-help="sales.fulfilment.ship.save" disabled={badUrl}
            onClick={() => onSave({ carrier: carrier.trim() || undefined, trackingNo: trackingNo.trim() || undefined, trackingUrl: trackingUrl.trim() || undefined, notes: notes.trim() || undefined })}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

const StatusDialog: React.FC<{ to: ShipmentStatus; challanDate: string; shippedOn?: string; onClose: () => void; onSave: (dto: { status: ShipmentStatus; shippedOn?: string; deliveredOn?: string; deliveredTo?: string }) => void }> =
  ({ to, challanDate, shippedOn, onClose, onSave }) => {
    const today = toLocalIsoDate();
    const [date, setDate] = useState(today);
    const [deliveredTo, setDeliveredTo] = useState('');
    const needsDate = to === 'SHIPPED' || to === 'DELIVERED';
    const min = to === 'SHIPPED' ? challanDate : (shippedOn ?? challanDate);
    const dateBad = needsDate && (!date || date > today || (min && date < min));
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-help-page="sales.fulfilment.status.page">
        <DialogTitle>{to === 'CANCELLED' ? 'Cancel shipment' : `Mark ${humanizeEnum(to).toLowerCase()}`}</DialogTitle>
        <DialogContent data-help-section="sales.fulfilment.status.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {needsDate && (
            <TextField data-help="sales.fulfilment.status.date" label={to === 'SHIPPED' ? 'Shipped on' : 'Delivered on'} type="date" value={date} sx={{ mt: 1 }}
              onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min, max: today }}
              error={!!dateBad} helperText={dateBad ? `Between ${formatDate(min)} and today` : undefined} />
          )}
          {to === 'DELIVERED' && <TextField data-help="sales.fulfilment.status.receivedBy" label="Received by (optional)" value={deliveredTo} onChange={e => setDeliveredTo(e.target.value)} inputProps={{ maxLength: 150 }} />}
          {to === 'CANCELLED' && <Typography>The shipment is kept in the history; a new one can be created afterwards.</Typography>}
          {to === 'RETURNED' && <Typography>Informational only: take the goods back with a customer return.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.fulfilment.status.back" onClick={onClose}>Back</Button>
          <Button variant="contained" color={to === 'CANCELLED' ? 'error' : 'primary'} data-help="sales.fulfilment.status.confirm" disabled={!!dateBad}
            onClick={() => onSave({
              status: to,
              shippedOn: to === 'SHIPPED' ? date : undefined,
              deliveredOn: to === 'DELIVERED' ? date : undefined,
              deliveredTo: to === 'DELIVERED' ? (deliveredTo.trim() || undefined) : undefined,
            })}>Confirm</Button>
        </DialogActions>
      </Dialog>
    );
  };

export default ChallanFulfilmentTab;
